import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Grid,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {
  type ByokProviderId,
  type ByokQuizQuestion,
  type ByokTopicCard,
  type GuestByokCatalogDTO,
  fetchByokCatalog,
  fetchByokChat,
  fetchByokContent,
  fetchByokQuiz,
  fetchByokSubtopics,
  fetchByokTopics,
  getOrCreateGuestByokSessionId,
  submitGuestByokEmail,
} from '../services/guestByokApi';

type Step =
  | 'provider'
  | 'country'
  | 'year'
  | 'subject'
  | 'topics'
  | 'subtopics'
  | 'content'
  | 'quiz'
  | 'quizResults';

const SS_PROVIDER = 'guestByokProvider';
const SS_KEY = 'guestByokApiKey';

/** Synchronous store for BYOK request context (avoids stale React state when firing API in the same click handler). */
type ByokCtxSnapshot = { countryLabel: string; yearLabel: string; subjectName: string };

const BYOK_AI_WAIT_MESSAGES = [
  'AI is thinking…',
  'Building your response…',
  'Still working on it…',
  'Larger responses can take up to a minute.',
  'Almost there…',
];

function GuestByokAiWait({ active, variant = 'centered' }: { active: boolean; variant?: 'centered' | 'inline' }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) {
      setIdx(0);
      return undefined;
    }
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % BYOK_AI_WAIT_MESSAGES.length);
    }, 2800);
    return () => window.clearInterval(id);
  }, [active]);
  if (!active) return null;
  if (variant === 'inline') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, py: 0.5 }}>
        <CircularProgress size={22} thickness={5} />
        <Typography variant="body2" color="text.secondary">
          {BYOK_AI_WAIT_MESSAGES[idx]}
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 4,
        px: 2,
      }}
    >
      <CircularProgress size={48} />
      <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 380 }}>
        {BYOK_AI_WAIT_MESSAGES[idx]}
      </Typography>
    </Box>
  );
}

function scoreAnswer(q: ByokQuizQuestion, raw: unknown): boolean {
  const t = (q.type || '').toUpperCase();
  if (t === 'TRUE_FALSE') {
    return raw === q.correctAnswer;
  }
  if (t === 'MCQ') {
    return typeof raw === 'number' && raw === q.correctIndex;
  }
  if (t === 'MULTIPLE_SELECT') {
    const user = Array.isArray(raw) ? [...(raw as number[])].sort((a, b) => a - b) : [];
    const exp = [...(q.correctIndices ?? [])].sort((a, b) => a - b);
    if (user.length !== exp.length) return false;
    return user.every((v, i) => v === exp[i]);
  }
  if (t === 'SHORT_ANSWER') {
    const s = String(raw ?? '')
      .trim()
      .toLowerCase();
    if (!s) return false;
    const ok = q.acceptableAnswers ?? [];
    return ok.some((a) => s.includes(a.trim().toLowerCase()) || a.trim().toLowerCase().includes(s));
  }
  return false;
}

const GuestByokPage: React.FC = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<GuestByokCatalogDTO | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('provider');
  const [provider, setProvider] = useState<ByokProviderId>('GEMINI');
  const [apiKey, setApiKey] = useState('');
  const [countryId, setCountryId] = useState<string | null>(null);
  const [yearId, setYearId] = useState<string | null>(null);
  const [subjectName, setSubjectName] = useState<string | null>(null);
  const byokCtxRef = useRef<ByokCtxSnapshot>({ countryLabel: '', yearLabel: '', subjectName: '' });
  const [topics, setTopics] = useState<ByokTopicCard[]>([]);
  const [parentTopic, setParentTopic] = useState<ByokTopicCard | null>(null);
  const [subtopics, setSubtopics] = useState<ByokTopicCard[]>([]);
  const [activeTopic, setActiveTopic] = useState<ByokTopicCard | null>(null);
  const [contentMd, setContentMd] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [quizQuestions, setQuizQuestions] = useState<ByokQuizQuestion[]>([]);
  const [quizAnswers, setQuizAnswers] = useState<unknown[]>([]);
  const [quizScore, setQuizScore] = useState<{ correct: number; total: number } | null>(null);
  /** True if user opened lesson from subtopic grid (back goes to subtopics). */
  const [contentFromSubtopics, setContentFromSubtopics] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadStatus, setLeadStatus] = useState<'idle' | 'sending' | 'sent' | 'skipped'>('idle');

  useEffect(() => {
    getOrCreateGuestByokSessionId();
  }, []);

  useEffect(() => {
    let c = false;
    fetchByokCatalog()
      .then((d) => {
        if (!c) setCatalog(d);
      })
      .catch(() => {
        if (!c) setCatalogError('Could not load catalog.');
      });
    return () => {
      c = true;
    };
  }, []);

  useEffect(() => {
    try {
      const p = sessionStorage.getItem(SS_PROVIDER) as ByokProviderId | null;
      const k = sessionStorage.getItem(SS_KEY);
      if (p && ['GEMINI', 'OPENAI', 'ANTHROPIC'].includes(p) && k) {
        setProvider(p);
        setApiKey(k);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const country = useMemo(() => catalog?.countries.find((x) => x.id === countryId), [catalog, countryId]);
  const year = useMemo(() => country?.years.find((y) => y.id === yearId), [country, yearId]);
  const telemetryIds = useMemo(() => ({ countryId, yearId }), [countryId, yearId]);

  const ctx = useCallback(() => {
    const r = byokCtxRef.current;
    return {
      countryLabel: (r.countryLabel || country?.label || '').trim(),
      yearLabel: (r.yearLabel || year?.label || '').trim(),
      subjectName: (r.subjectName || subjectName || '').trim(),
    };
  }, [country, year, subjectName]);

  const persistKey = () => {
    try {
      sessionStorage.setItem(SS_PROVIDER, provider);
      sessionStorage.setItem(SS_KEY, apiKey);
    } catch {
      /* ignore */
    }
  };

  const goBack = () => {
    setError(null);
    switch (step) {
      case 'provider':
        navigate('/guest');
        break;
      case 'country':
        setStep('provider');
        break;
      case 'year':
        setStep('country');
        break;
      case 'subject':
        setStep('year');
        break;
      case 'topics':
        setTopics([]);
        setStep('subject');
        break;
      case 'subtopics':
        setSubtopics([]);
        setParentTopic(null);
        setStep('topics');
        break;
      case 'content':
        setContentMd('');
        setActiveTopic(null);
        setChatMessages([]);
        if (contentFromSubtopics) setStep('subtopics');
        else {
          setParentTopic(null);
          setStep('topics');
        }
        setContentFromSubtopics(false);
        break;
      case 'quiz':
        setStep('content');
        break;
      case 'quizResults':
        setStep('content');
        setQuizScore(null);
        break;
      default:
        break;
    }
  };

  const onContinueProvider = () => {
    if (!apiKey.trim()) {
      setError('Enter your API key.');
      return;
    }
    persistKey();
    setError(null);
    setStep('country');
  };

  const loadTopics = async () => {
    const { countryLabel, yearLabel, subjectName: subj } = byokCtxRef.current;
    const countryLabelT = countryLabel.trim();
    const yearLabelT = yearLabel.trim();
    const subjectForRequest = subj.trim();
    if (!countryLabelT || !yearLabelT || !subjectForRequest) {
      setError('Missing country, year, or subject. Go back and select again.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await fetchByokTopics(
        provider,
        apiKey,
        {
          countryLabel: countryLabelT,
          yearLabel: yearLabelT,
          subjectName: subjectForRequest,
        },
        telemetryIds
      );
      setTopics(list);
      setStep('topics');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const onSelectTopic = async (t: ByokTopicCard) => {
    setLoading(true);
    setError(null);
    setParentTopic(t);
    try {
      const children = await fetchByokSubtopics(
        provider,
        apiKey,
        {
          ...ctx(),
          parentTopicTitle: t.title,
          parentTopicDescription: t.description,
        },
        telemetryIds
      );
      if (children.length === 0) {
        setContentFromSubtopics(false);
        setActiveTopic(t);
        const md = await fetchByokContent(provider, apiKey, {
          ...ctx(),
          topicTitle: t.title,
          topicDescription: t.description,
        });
        setContentMd(md);
        setChatMessages([]);
        setStep('content');
      } else {
        setSubtopics(children);
        setStep('subtopics');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load subtopics');
    } finally {
      setLoading(false);
    }
  };

  const onSelectSubtopic = async (t: ByokTopicCard) => {
    setLoading(true);
    setError(null);
    setActiveTopic(t);
    setContentFromSubtopics(true);
    try {
      const parentCtx = parentTopic ? `Parent unit: ${parentTopic.title}. ${parentTopic.description ?? ''}` : '';
      const md = await fetchByokContent(
        provider,
        apiKey,
        {
          ...ctx(),
          topicTitle: t.title,
          topicDescription: t.description,
          parentContext: parentCtx,
        },
        telemetryIds
      );
      setContentMd(md);
      setChatMessages([]);
      setStep('content');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || !activeTopic) return;
    setLoading(true);
    setError(null);
    const nextUser = [...chatMessages, { role: 'user' as const, content: text }];
    setChatInput('');
    try {
      const reply = await fetchByokChat(
        provider,
        apiKey,
        {
          ...ctx(),
          topicTitle: activeTopic.title,
          topicContent: contentMd,
          messages: nextUser.map((m) => ({ role: m.role, content: m.content })),
        },
        telemetryIds
      );
      setChatMessages([...nextUser, { role: 'assistant', content: reply }]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Chat failed');
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    if (!activeTopic) return;
    setLoading(true);
    setError(null);
    try {
      const qs = await fetchByokQuiz(
        provider,
        apiKey,
        {
          ...ctx(),
          topicTitle: activeTopic.title,
          topicContent: contentMd,
        },
        telemetryIds
      );
      setQuizQuestions(qs);
      setQuizAnswers(qs.map(() => null));
      setStep('quiz');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to generate quiz');
    } finally {
      setLoading(false);
    }
  };

  const submitQuiz = () => {
    let correct = 0;
    quizQuestions.forEach((q, i) => {
      if (scoreAnswer(q, quizAnswers[i])) correct += 1;
    });
    setQuizScore({ correct, total: quizQuestions.length });
    setLeadStatus('idle');
    setLeadEmail('');
    setStep('quizResults');
  };

  const submitLeadEmail = async () => {
    const e = leadEmail.trim();
    if (!e) {
      setError('Enter an email or skip.');
      return;
    }
    setLeadStatus('sending');
    setError(null);
    try {
      await submitGuestByokEmail(e);
      setLeadStatus('sent');
    } catch (err: unknown) {
      setLeadStatus('idle');
      setError(err instanceof Error ? err.message : 'Could not save email');
    }
  };

  const renderQuizInput = (q: ByokQuizQuestion, index: number) => {
    const t = (q.type || '').toUpperCase();
    const val = quizAnswers[index];
    const setVal = (v: unknown) => {
      const next = [...quizAnswers];
      next[index] = v;
      setQuizAnswers(next);
    };

    if (t === 'TRUE_FALSE') {
      return (
        <RadioGroup
          row
          value={val === true ? 't' : val === false ? 'f' : ''}
          onChange={(_, v) => setVal(v === 't')}
        >
          <FormControlLabel value="t" control={<Radio />} label="True" />
          <FormControlLabel value="f" control={<Radio />} label="False" />
        </RadioGroup>
      );
    }
    if (t === 'MCQ' && q.options) {
      return (
        <RadioGroup value={val ?? ''} onChange={(_, v) => setVal(Number(v))}>
          {q.options.map((opt, j) => (
            <FormControlLabel key={j} value={String(j)} control={<Radio />} label={opt} />
          ))}
        </RadioGroup>
      );
    }
    if (t === 'MULTIPLE_SELECT' && q.options) {
      const selected = Array.isArray(val) ? (val as number[]) : [];
      const toggle = (j: number) => {
        const set = new Set(selected);
        if (set.has(j)) set.delete(j);
        else set.add(j);
        setVal([...set].sort((a, b) => a - b));
      };
      return (
        <FormGroup>
          {q.options.map((opt, j) => (
            <FormControlLabel
              key={j}
              control={<Checkbox checked={selected.includes(j)} onChange={() => toggle(j)} />}
              label={opt}
            />
          ))}
        </FormGroup>
      );
    }
    if (t === 'SHORT_ANSWER') {
      return (
        <TextField
          fullWidth
          size="small"
          value={(val as string) ?? ''}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Your answer"
        />
      );
    }
    return <Typography color="text.secondary">Unsupported type: {q.type}</Typography>;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={goBack} sx={{ mb: 2 }}>
        Back
      </Button>

      {catalogError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {catalogError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 4 }}>
        {step === 'provider' && (
          <>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Try with your own AI key
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your key is sent over HTTPS with each AI request only. We do not store it on our servers. You can clear it by
              closing the browser tab or removing session storage.
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Provider
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={provider}
              onChange={(_, v) => v && setProvider(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="GEMINI">Gemini</ToggleButton>
              <ToggleButton value="OPENAI">OpenAI</ToggleButton>
              <ToggleButton value="ANTHROPIC">Claude</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              fullWidth
              type="password"
              label="API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              sx={{ mb: 2 }}
              autoComplete="off"
            />
            <Button variant="contained" onClick={onContinueProvider}>
              Continue
            </Button>
          </>
        )}

        {step === 'country' && catalog && (
          <>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Select country
            </Typography>
            <Grid container spacing={2}>
              {catalog.countries.map((c) => (
                <Grid item xs={12} sm={4} key={c.id}>
                  <Card
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                    onClick={() => {
                      byokCtxRef.current.countryLabel = c.label;
                      byokCtxRef.current.yearLabel = '';
                      byokCtxRef.current.subjectName = '';
                      setCountryId(c.id);
                      setStep('year');
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6">{c.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {step === 'year' && country && (
          <>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              School year ({country.label})
            </Typography>
            <Grid container spacing={2}>
              {country.years.map((y) => (
                <Grid item xs={12} sm={6} md={4} key={y.id}>
                  <Card
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                    onClick={() => {
                      byokCtxRef.current.yearLabel = y.label;
                      byokCtxRef.current.subjectName = '';
                      setYearId(y.id);
                      setStep('subject');
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {y.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {step === 'subject' && year && (
          <>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Subject ({year.label})
            </Typography>
            <Grid container spacing={2}>
              {year.subjects.map((s) => (
                <Grid item xs={12} sm={6} md={4} key={s}>
                  <Card
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                    onClick={() => {
                      byokCtxRef.current.subjectName = s;
                      setSubjectName(s);
                      void loadTopics();
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {s}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <GuestByokAiWait active={loading} />
          </>
        )}

        {step === 'topics' && (
          <>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Topics
            </Typography>
            <GuestByokAiWait active={loading} />
            <Grid container spacing={2}>
              {topics.map((t) => (
                <Grid item xs={12} sm={6} md={4} key={t.id || t.title}>
                  <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }} onClick={() => void onSelectTopic(t)}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                        {t.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {step === 'subtopics' && parentTopic && (
          <>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              {parentTopic.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {parentTopic.description}
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Choose a subtopic
            </Typography>
            <GuestByokAiWait active={loading} />
            <Grid container spacing={2}>
              {subtopics.map((t) => (
                <Grid item xs={12} sm={6} md={4} key={t.id || t.title}>
                  <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }} onClick={() => void onSelectSubtopic(t)}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                        {t.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {step === 'content' && activeTopic && (
          <>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              {activeTopic.title}
            </Typography>
            <Button variant="outlined" sx={{ mb: 2 }} onClick={() => void startQuiz()} disabled={loading}>
              Start quiz on this topic
            </Button>
            <Divider sx={{ my: 2 }} />
            {loading && !contentMd ? (
              <GuestByokAiWait active />
            ) : (
              <Box className="markdown-body" sx={{ mb: 3 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                  {contentMd}
                </ReactMarkdown>
              </Box>
            )}
            {loading && !!contentMd && <GuestByokAiWait active variant="inline" />}
            <Typography variant="h6" sx={{ mb: 1 }}>
              Chat about this topic
            </Typography>
            <Box sx={{ maxHeight: 280, overflow: 'auto', mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              {chatMessages.map((m, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                  <strong>{m.role === 'user' ? 'You' : 'Tutor'}:</strong> {m.content}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask a follow-up…"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && void sendChat()}
              />
              <Button variant="contained" onClick={() => void sendChat()} disabled={loading}>
                Send
              </Button>
            </Box>
          </>
        )}

        {step === 'quiz' && (
          <>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Quiz
            </Typography>
            {quizQuestions.map((q, i) => (
              <Box key={i} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {i + 1}. {q.prompt}
                </Typography>
                {renderQuizInput(q, i)}
              </Box>
            ))}
            <Button variant="contained" onClick={submitQuiz}>
              Submit answers
            </Button>
          </>
        )}

        {step === 'quizResults' && quizScore && (
          <>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Results
            </Typography>
            <Typography variant="h6" sx={{ mb: 3 }}>
              You scored {quizScore.correct} / {quizScore.total}
            </Typography>
            <GuestByokAiWait active={loading} />
            {leadStatus === 'idle' && (
              <Box sx={{ mb: 3, maxWidth: 420 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Stay in the loop
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Optional: share your email for product updates. We never store your API key.
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={leadEmail}
                  onChange={(ev) => setLeadEmail(ev.target.value)}
                  sx={{ mb: 2 }}
                  autoComplete="email"
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Button variant="contained" onClick={() => void submitLeadEmail()} disabled={leadStatus === 'sending'}>
                    {leadStatus === 'sending' ? 'Sending…' : 'Submit'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setLeadStatus('skipped');
                    }}
                  >
                    Skip
                  </Button>
                </Box>
              </Box>
            )}
            {leadStatus === 'sent' && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Thanks — we saved your email.
              </Alert>
            )}
            {leadStatus === 'skipped' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No problem. You can close this tab anytime.
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setQuizScore(null);
                  void startQuiz();
                }}
                disabled={loading}
              >
                Try another quiz
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setQuizScore(null);
                  setQuizQuestions([]);
                  setStep('content');
                }}
              >
                Back to topic
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default GuestByokPage;
